import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, yen, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { StorefrontHeader } from "../components/StorefrontHeader";
import { AuthDialog } from "../components/AuthDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { User, LogOut, Receipt, RotateCcw, MessageCircle } from "lucide-react";
import { waLink } from "../lib/wa";

const STATUS_KEY = { new: "status_new", processing: "status_processing", done: "status_done", cancelled: "status_cancelled" };

export default function Akun() {
  const { t } = useI18n();
  const { user, ready, logout, saveProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [orders, setOrders] = useState([]);
  const [storeWa, setStoreWa] = useState("");
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setStoreWa(data.whatsapp_number || data.phone || ""));
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
    api.get("/auth/orders").then(({ data }) => setOrders(data));
  }, [user]);

  if (!ready) return <div className="p-8 text-muted-foreground">{t("loading")}</div>;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <StorefrontHeader active="akun" />
      <div className="w-full max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold">{t("my_account")}</h1>
          <p className="text-sm text-muted-foreground">{t("account_sub")}</p>
        </div>

        {!user ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground" data-testid="account-login-required">
              {t("login_required")}
            </p>
            <button
              data-testid="account-login-cta"
              onClick={() => setAuthOpen(true)}
              className="text-sm text-primary underline"
            >
              {t("login")}
            </button>
            <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
          </div>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="account-profile">
              <p className="font-display font-semibold flex items-center gap-2">
                <User size={18} className="text-primary" /> {user.email}
              </p>
              <Input
                data-testid="account-name-input"
                placeholder={t("full_name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                data-testid="account-phone-input"
                placeholder={t("cust_phone")}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <Input
                data-testid="account-address-input"
                placeholder={t("cust_address")}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
              <div className="flex gap-2">
                <Button
                  data-testid="account-save-btn"
                  onClick={async () => {
                    try {
                      await saveProfile(form);
                      toast.success(t("saved"));
                    } catch (e) {
                      toast.error(errMsg(e, "Gagal menyimpan"));
                    }
                  }}
                >
                  {t("save")}
                </Button>
                <Button variant="outline" data-testid="account-logout-btn" onClick={logout}>
                  <LogOut size={15} className="mr-1" /> {t("logout")}
                </Button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="account-orders">
              <p className="font-display font-semibold flex items-center gap-2">
                <Receipt size={18} className="text-primary" /> {t("order_history")}
              </p>
              {orders.length === 0 && (
                <p className="text-sm text-muted-foreground">{t("no_data")}</p>
              )}
              {orders.map((o) => (
                <div
                  key={o.id}
                  data-testid={`account-order-${o.order_no}`}
                  className="flex items-center justify-between gap-3 border-t pt-3 first:border-0 first:pt-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{o.order_no}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleString("ja-JP")} ·{" "}
                      {o.items.length} item · {t(STATUS_KEY[o.status] || "status_new")}
                    </p>
                  </div>
                  <span className="num font-semibold">{yen(o.total)}</span>
                  {storeWa && (
                    <a
                      href={waLink(
                        storeWa,
                        `Halo, saya ingin menanyakan pesanan ${o.order_no} (total ${yen(o.total)}).`
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`account-wa-${o.order_no}`}
                      className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors duration-200"
                    >
                      <MessageCircle size={14} /> {t("contact_store_wa")}
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`reorder-btn-${o.order_no}`}
                    onClick={() => {
                      localStorage.setItem(
                        "khf_reorder",
                        JSON.stringify({ channel: o.channel, items: o.items })
                      );
                      navigate(o.channel === "resto" ? "/resto" : "/toko");
                    }}
                  >
                    <RotateCcw size={14} className="mr-1" /> {t("reorder")}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
